import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import Icon from "components/icon/Icon";
import "./browseTree.scss";

export interface IBrowseTreeNode {
  id: string;
  name: string;
  label: React.ReactNode;
  children?: IBrowseTreeNode[];
}

export interface IBrowseTreeProps {
  nodes: IBrowseTreeNode[];
  ariaLabel?: string;
  isSelected: (id: string) => boolean;
  onSelect: (id: string) => void;
  expandOnSelect?: boolean;
  className?: string;
}

function handleActivationKeyDown(event: React.KeyboardEvent, action: () => void) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
}

interface IChevronButtonProps {
  isExpanded: boolean;
  onChevronClick: (event: React.MouseEvent) => void;
  ariaLabel: string;
  controlsId?: string;
}

function ChevronButton(props: IChevronButtonProps) {
  const { isExpanded, onChevronClick, ariaLabel, controlsId } = props;

  return (
    <button
      type="button"
      className={`browse-tree__chevron${isExpanded ? " browse-tree__chevron--expanded" : ""}`}
      onClick={onChevronClick}
      aria-expanded={isExpanded}
      aria-label={ariaLabel}
      aria-controls={isExpanded ? controlsId : undefined}
    >
      <Icon icon={faChevronRight} size="small" />
    </button>
  );
}

interface IBrowseTreeNodeRowProps {
  node: IBrowseTreeNode;
  depth: number;
  isSelected: (id: string) => boolean;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  onToggleExpanded: (id: string) => void;
}

function BrowseTreeNodeRow(props: IBrowseTreeNodeRowProps) {
  const { node, depth, isSelected, onSelect, expandedIds, onToggleExpanded } = props;
  const { t } = useTranslation();
  const children = node.children ?? [];
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const active = isSelected(node.id);
  const childrenId = `browse-tree-node-${node.id}-children`;
  const rowClassName = `browse-tree__row${active ? " browse-tree__row--active" : ""}`;

  function handleSelect() {
    onSelect(node.id);
  }

  function handleChevronClick(event: React.MouseEvent) {
    event.stopPropagation();
    onToggleExpanded(node.id);
  }

  return (
    <React.Fragment>
      {hasChildren ? (
        <div
          className={rowClassName}
          role="button"
          tabIndex={0}
          aria-current={active ? "true" : undefined}
          onClick={handleSelect}
          onKeyDown={event => handleActivationKeyDown(event, handleSelect)}
        >
          <div
            className="browse-tree__row-inner"
            style={{ "--browse-tree-depth": depth } as React.CSSProperties}
          >
            <ChevronButton
              isExpanded={isExpanded}
              onChevronClick={handleChevronClick}
              ariaLabel={
                isExpanded
                  ? t("Collapse {{name}}", { name: node.name })
                  : t("Expand {{name}}", { name: node.name })
              }
              controlsId={childrenId}
            />
            <span className="browse-tree__label">{node.label}</span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={rowClassName}
          aria-current={active ? "true" : undefined}
          onClick={handleSelect}
        >
          <div
            className="browse-tree__row-inner browse-tree__row-inner--leaf"
            style={{ "--browse-tree-depth": depth } as React.CSSProperties}
          >
            <span className="browse-tree__label">{node.label}</span>
          </div>
        </button>
      )}

      {isExpanded && hasChildren ? (
        <div id={childrenId}>
          {children.map(child => (
            <BrowseTreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              isSelected={isSelected}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </div>
      ) : null}
    </React.Fragment>
  );
}

function findNode(nodes: IBrowseTreeNode[], id: string): IBrowseTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }

    if (node.children) {
      const found = findNode(node.children, id);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
}

export default function BrowseTree(props: IBrowseTreeProps) {
  const {
    nodes,
    ariaLabel,
    isSelected,
    onSelect,
    expandOnSelect = true,
    className,
  } = props;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleSelect(id: string) {
    onSelect(id);

    if (expandOnSelect) {
      const selectedNode = findNode(nodes, id);
      if (selectedNode?.children?.length) {
        setExpandedIds(prev => new Set(prev).add(id));
      }
    }
  }

  const treeContent = nodes.map(node => (
    <BrowseTreeNodeRow
      key={node.id}
      node={node}
      depth={0}
      isSelected={isSelected}
      onSelect={handleSelect}
      expandedIds={expandedIds}
      onToggleExpanded={toggleExpanded}
    />
  ));

  if (ariaLabel) {
    return (
      <nav className={className ? `browse-tree ${className}` : "browse-tree"} aria-label={ariaLabel}>
        {treeContent}
      </nav>
    );
  }

  return <>{treeContent}</>;
}
